import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"
import { useTakerAuth, useSWRWithAuth, useGetInvoker } from "@today/api"
import { Client, PrepaidPoints } from "@today/api/taker"
import { LoadingOverlay } from "../components/common/LoadingOverlay"
import { useAnonymousPermissionStatus, useUserInfo } from "@today/auth"

type PointsInfo = {
  points: PrepaidPoints
  isUsingPoints: boolean
  refreshPoints: () => Promise<void>
  hasNegativePoints: boolean
  hasNonPositivePoints: boolean
}

const PointsContext = createContext<PointsInfo>({
  points: {
    clientId: "",
    remainingPoints: 0,
  },
  isUsingPoints: false,
  hasNegativePoints: false,
  hasNonPositivePoints: false,
  refreshPoints: async () => {},
})

export const PointsProvider = ({ children }: PropsWithChildren<{}>) => {
  const { allowAnonymous } = useAnonymousPermissionStatus()
  const { clientId } = useUserInfo()
  const taker = useTakerAuth()

  const [client, setClient] = useState<Client>()

  useEffect(() => {
    if (!clientId) {
      setClient(undefined)
      return
    }
    ;(async () => {
      const client = await taker.retrieveClient()
      setClient(client)
    })()
  }, [clientId])

  const shouldUsePoints =
    !!client && client.enableBilling && client.billingType === "PREPAID"
  const { data: points, mutate } = useGetInvoker<PrepaidPoints>(
    shouldUsePoints && clientId && taker.getClientPrepaidPoints(clientId)
  )
  const isLoading = (shouldUsePoints && !points) || (!allowAnonymous && !client)

  const pointsWithDefault = useMemo(
    () =>
      points ?? {
        clientId: clientId ?? "",
        remainingPoints: 0,
      },
    [clientId, points]
  )

  const refresh = useCallback(async () => {
    await mutate()
  }, [mutate])

  return (
    <PointsContext.Provider
      value={{
        points: pointsWithDefault,
        isUsingPoints: shouldUsePoints,
        refreshPoints: refresh,
        hasNegativePoints:
          shouldUsePoints && pointsWithDefault.remainingPoints < 0,
        hasNonPositivePoints:
          shouldUsePoints && pointsWithDefault.remainingPoints <= 0,
      }}
    >
      {/* clients/me 가 계속 실패하면(예: 파트너가 아닌 계정으로 로그인) 여기서 빠져나갈 수
          없으므로, 인증 게이트와 동일하게 세션 초기화 경로를 제공한다. */}
      {isLoading ? <LoadingOverlay enableSessionReset /> : children}
    </PointsContext.Provider>
  )
}

export function usePoints() {
  return useContext(PointsContext)
}
